/**
 * #830 Part C — labels for the values the recipients API sends in English: a
 * staging row's source, status and resolution, and a recipient's status.
 *
 * Pending Actions, its conflict dialog, the Microsoft 365 conflict panel and
 * the import details panel used to print them as sent ("Conflict", "CSV
 * Import", "User Approved", "Bounced"). Each label is a literal t() call, so
 * the static-keys guard checks every key exists.
 *
 * Product names (Microsoft 365, Google Workspace, Okta, AD Syncer, HFv2) are
 * names, not words, and are shown as they are (docs/LOCALE.md §3.3). A value
 * this build does not know is shown as sent, never as a key path, and never
 * throws: the API can gain a value before the web does (CLAUDE.md, "the sixth
 * payload type").
 */
import { SOURCE_DISPLAY_INFO } from '@/lib/types/recipients'

export type Translate = (key: string, params?: Record<string, string | number>) => string

const own = (map: object, key: string) => Object.prototype.hasOwnProperty.call(map, key)

/** A staging row's or a recipient's source ('CSV', 'Microsoft 365', the pgEnum 'Microsoft O365'…). */
export function sourceTypeLabel(t: Translate, source: string | null | undefined): string {
  switch (source) {
    case 'Manual':
      return t('recipients.sourceType.manual')
    case 'CSV':
      return t('recipients.sourceType.csv')
    case 'LDAP':
      return t('recipients.sourceType.ldap')
    case 'Unknown':
      return t('recipients.sourceType.unknown')
    default:
      if (!source) return ''
      // A product: its display name ('Microsoft O365' → 'Microsoft 365'), or the value as sent.
      return own(SOURCE_DISPLAY_INFO, source)
        ? SOURCE_DISPLAY_INFO[source as keyof typeof SOURCE_DISPLAY_INFO].label
        : source
  }
}

/** A staging row's status (Title Case, the staging_status pgEnum). */
export function stagingStatusLabel(t: Translate, status: string | null | undefined): string {
  switch (status) {
    case 'New':
      return t('recipients.stagingStatus.new')
    case 'Duplicate':
      return t('recipients.stagingStatus.duplicate')
    case 'Conflict':
      return t('recipients.stagingStatus.conflict')
    case 'Invalid':
      return t('recipients.stagingStatus.invalid')
    case 'Applied':
      return t('recipients.stagingStatus.applied')
    case 'Skipped':
      return t('recipients.stagingStatus.skipped')
    case 'Rejected':
      return t('recipients.stagingStatus.rejected')
    default:
      return status ?? ''
  }
}

/** A staging row's resolution ('Pending', 'Auto Applied', 'User Approved'…). */
export function stagingResolutionLabel(t: Translate, resolution: string | null | undefined): string {
  switch (resolution) {
    case 'Pending':
      return t('recipients.stagingResolution.pending')
    case 'Auto Applied':
      return t('recipients.stagingResolution.autoApplied')
    case 'User Approved':
      return t('recipients.stagingResolution.userApproved')
    case 'User Merged':
      return t('recipients.stagingResolution.userMerged')
    case 'User Rejected':
      return t('recipients.stagingResolution.userRejected')
    case 'Auto Skipped':
      return t('recipients.stagingResolution.autoSkipped')
    default:
      return resolution ?? ''
  }
}

/** A recipient's status ('Active', 'Inactive', 'Bounced', 'Unsubscribed'). */
export function recipientStatusLabel(t: Translate, status: string | null | undefined): string {
  switch (status) {
    case 'Active':
      return t('common.active')
    case 'Inactive':
      return t('common.inactive')
    case 'Bounced':
      return t('recipients.recipientStatus.bounced')
    case 'Unsubscribed':
      return t('recipients.recipientStatus.unsubscribed')
    default:
      return status ?? ''
  }
}
